import { connect, ConnectedProps } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom-v5-compat';
import { isPristine, isValid, submit } from 'redux-form';

import { VERIFICATION_TEMPLATE_EDITOR_FORM_NAME } from 'dashboard/apps/VerificationTemplateEditor';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { GovernmentIdCoverage } from 'dashboard/models/government-id-coverage';
import { VerificationTemplateVersion } from 'dashboard/models/verification-template-version';
import { setGovernmentIdCoverage } from 'dashboard/redux/actions/government-id-coverage';
import { showMessage } from 'dashboard/redux/actions/messages';
import { clearVerificationTemplate } from 'dashboard/redux/actions/verification-template';
import { RootState } from 'dashboard/redux/store';
import {
  discardDraftVerificationTemplateVersion,
  getEditorPath,
  loadLatestPublishedVerificationTemplateVersion,
  loadMatchRequirementProfiles,
  loadVerificationTemplateSchema,
  loadVerificationTemplateVersion,
  loadVerificationTemplateVersionDiff,
  publishVerificationTemplateVersion,
  saveVtmplv,
} from 'dashboard/redux/thunk-actions/verification-template';
import { ReferencedIdTuple } from 'dashboard/views/DashboardInquiryTemplatesShowView/utils';
import DashboardVerificationTemplateShowView from 'dashboard/views/DashboardVerificationTemplateShowView';
import ApiResponseError, { ServerError } from 'lib/errors/ApiResponseError';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useIsPersonerd from 'lib/hooks/useIsPersonerd';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import { ApiEndpoint } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const NETWORK_ERROR_MESSAGE_DEFAULT = 'We are experiencing network issues. Please try again.';

const getServerErrorMessage = (error: ServerError): string => {
  const { title, details } = error;
  return details ? `${title}: ${details.toString()}` : title;
};

type Params = {
  verificationTemplateId: string;
  verificationTemplateVersionId: string;
};

const DashboardVerificationTemplatesShowRoute = (props: PropsFromRedux) => {
  const { verificationTemplateVersion, verificationTemplate } = props;
  const navigate = useNavigate();
  useOnMountAndUnmount(props.clearVerificationTemplate);

  const { verificationTemplateVersionId, verificationTemplateId } = useParams<Params>() as Params;

  const shouldDisplayServerErrors = useIsPersonerd();
  const handleInternalServerErrorDisplay = (err: ApiResponseError) => {
    if (shouldDisplayServerErrors) {
      err.errors?.forEach((serverError) => {
        props.showMessage(getServerErrorMessage(serverError), MessageType.Notice);
      });
    }
  };

  // load the VTV specified in the URL and the relevant match requirement profiles
  const { loading: loadingVerificationTemplateVersion } = useApiLoader({
    loader: async () => {
      await props.loadVerificationTemplateVersion(verificationTemplateVersionId);
      const coverageResponse = await SessionClientDashboard.get<GovernmentIdCoverage>(
        ApiEndpoint.VerificationGovernmentId,
        'coverage',
        '',
      );
      props.setGovernmentIdCoverage(coverageResponse);
    },
    deps: [props.loadVerificationTemplateVersion, verificationTemplateVersionId],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load verification template.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: loadingMatchRequirementProfiles } = useApiLoader({
    loader: async () => {
      await props.loadMatchRequirementProfiles(verificationTemplateVersionId);
    },
    deps: [props.loadMatchRequirementProfiles, verificationTemplateVersionId],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load match requirement profiles.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: loadingSchema } = useApiLoader({
    loader: async () => {
      await props.loadVerificationTemplateSchema(verificationTemplateVersionId);
    },
    deps: [props.loadVerificationTemplateSchema],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load verification template schema.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  // handler for when a user publishes a verification template version
  const {
    callback: handlePublishVerificationTemplateVersion,
    loading: isPublishing,
    error: publishVerificationTemplateVersionError,
  } = useApiCallback(async () => {
    await props.publishVerificationTemplateVersion(
      verificationTemplateVersionId,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      verificationTemplateVersion.type,
    );
    props.showMessage('Your draft was successfully published.', MessageType.Success);
    navigate(getEditorPath(verificationTemplateId, verificationTemplateVersionId), {
      replace: true,
    });
  }, [
    props.publishVerificationTemplateVersion,
    verificationTemplateVersionId,
    verificationTemplateVersion,
  ]);

  useApiErrorHandler({
    error: publishVerificationTemplateVersionError,
    onError: (err) => {
      if (isPermissionsError(err)) {
        props.showMessage(permissionsErrorMessage(err), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to publish template draft, please try again.',
          MessageType.Failure,
        );
      }
      handleInternalServerErrorDisplay(err);
    },
    onNetworkError: () => props.showMessage(NETWORK_ERROR_MESSAGE_DEFAULT, MessageType.Failure),
  });

  // handler for when a user selects a different version of the verification template from the UI
  const {
    callback: handleSelectVerificationTemplateVersion,
    loading: loadingSelectedVerificationTemplateVersion,
    error: _selectVerificationTemplateVersionError,
  } = useApiCallback(
    async (verificationTemplateVersionId: string) => {
      await props.loadVerificationTemplateVersion(verificationTemplateVersionId);
      navigate(getEditorPath(verificationTemplateId, verificationTemplateVersionId), {
        replace: true,
      });
    },
    [props.loadVerificationTemplateVersion, verificationTemplateId, history],
  );

  const {
    callback: handleDiscardVerificationTemplateVersion,
    loading: isDiscarding,
    error: _discardVerificationTemplateVersionError,
  } = useApiCallback(async () => {
    const latestPublishedVersionId = (await props.discardDraftVerificationTemplateVersion(
      verificationTemplateVersionId,
    )) as string;
    props.showMessage('Your draft was successfully discarded.', MessageType.Success);
    if (latestPublishedVersionId) {
      handleSelectVerificationTemplateVersion(latestPublishedVersionId);
    }
  }, [
    props.discardDraftVerificationTemplateVersion,
    handleSelectVerificationTemplateVersion,
    verificationTemplateVersionId,
  ]);

  const {
    callback: handleSaveVerificationTemplateVersion,
    loading: isUpdating,
    error: saveVerificationTemplateVersionError,
  } = useApiCallback(
    async (verificationTemplateVersion: VerificationTemplateVersion) => {
      const { updatedId } = (await props.saveVtmplv(
        verificationTemplateVersion,
      )) as ReferencedIdTuple;
      props.showMessage('Your draft was successfully saved.', MessageType.Success);
      // reload form with updated version
      if (updatedId) {
        handleSelectVerificationTemplateVersion(updatedId);
      }
    },
    [props.saveVtmplv, handleSelectVerificationTemplateVersion],
  );

  useApiErrorHandler({
    error: saveVerificationTemplateVersionError,
    onError: (err) => {
      if (isPermissionsError(err)) {
        props.showMessage(permissionsErrorMessage(err), MessageType.Failure);
      } else if (err.status === 422 && err.errors?.[0].code === 'multiple_draft_error') {
        props.showMessage(
          'A draft already exists on this template. Please reload the page and try again.',
          MessageType.Failure,
        );
      } else {
        props.showMessage('Failed to update draft, please try again.', MessageType.Failure);
      }
      handleInternalServerErrorDisplay(err);
    },
    onNetworkError: () => props.showMessage(NETWORK_ERROR_MESSAGE_DEFAULT, MessageType.Failure),
  });

  const {
    callback: handleLoadVersionDiff,
    loading: loadingVersionDiff,
    error: _loadVersionDiffError,
  } = useApiCallback(async () => {
    const publishedVersionId =
      verificationTemplate?.relationships?.['latest-published-version']?.data?.id;
    if (publishedVersionId) {
      await props.loadVerificationTemplateVersionDiff(verificationTemplateId);
    } else {
      props.showMessage(
        'Unable to load changes since last published version.',
        MessageType.Failure,
      );
    }
  }, [verificationTemplate]);

  const {
    callback: handleLoadLatestPublishedVersion,
    loading: loadingLatestPublishedVersion,
    error: _loadLatestPublishedVersionError,
  } = useApiCallback(async () => {
    const publishedVersionId =
      verificationTemplate?.relationships?.['latest-published-version']?.data?.id;
    if (publishedVersionId) {
      await props.loadLatestPublishedVerificationTemplateVersion(publishedVersionId);
    } else {
      props.showMessage(
        'Unable to load changes since last published version.',
        MessageType.Failure,
      );
    }
  }, [verificationTemplate]);

  const isLoading =
    loadingMatchRequirementProfiles ||
    loadingSchema ||
    loadingSelectedVerificationTemplateVersion ||
    loadingVerificationTemplateVersion ||
    loadingLatestPublishedVersion ||
    loadingVersionDiff;

  return (
    <DashboardVerificationTemplateShowView
      verificationTemplate={props.verificationTemplate}
      verificationTemplateVersion={props.verificationTemplateVersion}
      latestPublishedVerificationTemplateVersion={props.latestPublishedVerificationTemplateVersion}
      verificationTemplateVersionDiff={props.verificationTemplateVersionDiff}
      matchRequirementProfiles={props.matchRequirementProfiles}
      schema={props.schema}
      pendingStates={{
        isDiscarding,
        isLoading,
        isPublishing,
        isUpdating,
      }}
      isFormPristine={props.isFormPristine}
      isFormValid={props.isFormValid}
      handleDiscardDraft={handleDiscardVerificationTemplateVersion}
      handlePublishDraft={handlePublishVerificationTemplateVersion}
      handleSave={handleSaveVerificationTemplateVersion}
      handleLoadVersionDiff={handleLoadVersionDiff}
      handleLoadLatestPublishedVersion={handleLoadLatestPublishedVersion}
      submit={props.submit}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    verificationTemplate: state.verificationTemplate.verificationTemplate,
    verificationTemplateVersion: state.verificationTemplate.verificationTemplateVersion,
    latestPublishedVerificationTemplateVersion:
      state.verificationTemplate.latestPublishedVerificationTemplateVersion,
    matchRequirementProfiles: state.verificationTemplate.matchRequirementProfiles,
    schema: state.verificationTemplate.schema,
    verificationTemplateVersionDiff: state.verificationTemplate.verificationTemplateVersionDiff,
    isFormPristine: isPristine(VERIFICATION_TEMPLATE_EDITOR_FORM_NAME)(state),
    isFormValid: isValid(VERIFICATION_TEMPLATE_EDITOR_FORM_NAME)(state),
  };
};

const mapDispatchToProps = {
  setGovernmentIdCoverage,
  loadVerificationTemplateVersion,
  loadMatchRequirementProfiles,
  loadVerificationTemplateSchema,
  loadVerificationTemplateVersionDiff,
  loadLatestPublishedVerificationTemplateVersion,
  publishVerificationTemplateVersion,
  discardDraftVerificationTemplateVersion,
  saveVtmplv,
  clearVerificationTemplate,
  showMessage,
  submit: () => submit(VERIFICATION_TEMPLATE_EDITOR_FORM_NAME),
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardVerificationTemplatesShowRoute);
